import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, Mail, User, LogIn, UserPlus, ArrowRight, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GuestCheckoutModal Component - REDESIGNED for booking-first flow
 * 
 * FLOW:
 * 1. Collect ALL required contact/profile fields: name, email, phone, address
 * 2. Create provisional account silently with complete profile data
 * 3. Log user in automatically (no password needed yet)
 * 4. Continue SAME booking flow as signed-in users:
 *    - Verification page (licence, selfie)
 *    - Accept host terms
 *    - Submit booking request
 * 5. After booking submitted: Show success, prompt to check email for password setup
 * 
 * All collected details are stored on the standard user profile fields,
 * reused by the booking validation flow without needing re-entry.
 */

// Australian states for dropdown
const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
];

const GuestCheckoutModal = ({ 
  isOpen, 
  onClose, 
  vehicleId,
  vehicleName,
  selectedDates,
  onGuestAccountCreated  // Called after silent login completes
}) => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth(); // Use silent login function
  const [mode, setMode] = useState('choose'); // 'choose', 'guest'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    // P1 GUEST CHECKOUT: Address fields required for booking
    address_line: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Build the return URL with booking context
  const buildReturnUrl = () => {
    return `/vehicles/${vehicleId}`;
  };

  // Handle "Sign In" button - preserve listing context
  const handleSignIn = () => {
    onClose();
    navigate('/login', { 
      state: { 
        from: { pathname: buildReturnUrl() },
        bookingIntent: true 
      } 
    });
  };

  // Handle "Create Account" button - preserve listing context
  const handleCreateAccount = () => {
    onClose();
    navigate('/register', { 
      state: { 
        from: { pathname: buildReturnUrl() },
        bookingIntent: true 
      } 
    });
  };

  // NEW FLOW: Create guest account and log in silently
  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!formData.full_name.trim()) {
      setError('Please enter your full name');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (!formData.address_line.trim()) {
      setError('Please enter your street address');
      return;
    }
    if (!formData.city.trim()) {
      setError('Please enter your city/suburb');
      return;
    }
    if (!formData.state) {
      setError('Please select your state');
      return;
    }
    if (!formData.postcode.trim()) {
      setError('Please enter your postcode');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/guest-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          full_name: formData.full_name.trim(),
          phone: formData.phone.trim() || null,
          // P1 GUEST CHECKOUT: Include address fields for booking validation
          address_line: formData.address_line.trim(),
          city: formData.city.trim(),
          state: formData.state,
          postcode: formData.postcode.trim(),
          country: formData.country,
          // Booking context
          vehicle_id: vehicleId,
          booking_context: {
            vehicle_id: vehicleId,
            vehicle_name: vehicleName,
            selected_dates: selectedDates
          }
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response JSON:', jsonError);
        data = { detail: 'Failed to process server response. Please try again.' };
      }

      if (!response.ok) {
        // Handle duplicate email / account already exists
        if (response.status === 409 || 
            (response.status === 400 && (
              data.detail?.toLowerCase().includes('already registered') ||
              data.detail?.toLowerCase().includes('already exists') ||
              data.detail?.toLowerCase().includes('email is already')
            ))) {
          setError('An account with this email already exists. Please sign in or reset your password.');
          toast.info('Account already exists - redirecting to login...', { duration: 3000 });
          setTimeout(() => {
            onClose();
            navigate('/login', { 
              state: { 
                from: buildReturnUrl(),
                email: formData.email,
                bookingIntent: true,
                message: 'Please sign in to continue with your booking.'
              } 
            });
          }, 2500);
          return;
        }
        
        const errorMessage = data.detail || data.message || 'Failed to create account. Please try again.';
        throw new Error(errorMessage);
      }

      // ✅ SUCCESS - Account created with token
      // CRITICAL: Log the user in silently using the returned token
      if (data.token) {
        
        // Use the new silent login function
        await loginWithToken(data.token, {
          user_id: data.user_id,
          email: data.email,
          full_name: formData.full_name,
          verification_status: 'unverified',
          roles: ['customer'],
          password_reset_required: true
        });
        
        toast.success('Account created! Let\'s verify your identity to complete the booking.');
        
        // Notify parent and close modal
        if (onGuestAccountCreated) {
          onGuestAccountCreated(data);
        }
        handleClose();
        
        // Navigate to verification - existing flow will handle the rest
        // The user is now logged in, so after verification they can use the normal booking modal
        navigate('/verification', { 
          state: { 
            from: buildReturnUrl(),
            isGuestCheckout: true,
            bookingIntent: true
          } 
        });
      } else {
        throw new Error('No token received from server');
      }

    } catch (err) {
      console.error('[GuestCheckout] Error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset modal state when closing
  const handleClose = () => {
    setMode('choose');
    setError('');
    setFormData({ 
      email: '', 
      full_name: '', 
      phone: '',
      address_line: '',
      city: '',
      state: '',
      postcode: '',
      country: 'Australia'
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        {mode === 'choose' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Ready to Book?</DialogTitle>
              <DialogDescription>
                Choose how you'd like to continue. No payment until the host approves your request.
              </DialogDescription>
            </DialogHeader>

            <div className="py-6 space-y-4">
              {/* Continue as Guest - Primary action */}
              <button
                onClick={() => setMode('guest')}
                className="w-full p-4 text-left border-2 border-[#004B8D] bg-blue-50/50 rounded-xl hover:bg-blue-50 transition-all group"
                data-testid="guest-checkout-option"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#004B8D] flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 mb-1">Continue as Guest</p>
                    <p className="text-sm text-slate-500">
                      Quick signup - we'll create your account as you book
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#004B8D] mt-2" />
                </div>
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-sm text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Sign In */}
              <button
                onClick={handleSignIn}
                className="w-full p-4 text-left border border-slate-200 rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-all group"
                data-testid="signin-option"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200">
                    <LogIn className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 mb-1">Sign In</p>
                    <p className="text-sm text-slate-500">
                      Already have an account? Sign in to continue.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 mt-2" />
                </div>
              </button>

              {/* Create Account */}
              <button
                onClick={handleCreateAccount}
                className="w-full p-4 text-left border border-slate-200 rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-all group"
                data-testid="create-account-option"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 mb-1">Create Account</p>
                    <p className="text-sm text-slate-500">
                      New to HireHub? Create your account with password.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 mt-2" />
                </div>
              </button>
            </div>
            
            {/* Trust Footer */}
            <div className="pt-3 border-t border-slate-100 text-center">
              <p className="text-xs text-slate-400">
                Secure checkout • ID verification required • No charge until approved
              </p>
            </div>
          </>
        )}

        {mode === 'guest' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Quick Signup</DialogTitle>
              <DialogDescription>
                Enter your details to continue with booking. You'll verify your identity next.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleGuestSubmit} className="py-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Personal Details Section */}
              <div className="space-y-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Personal Details</p>
                
                <div className="space-y-1.5">
                  <Label htmlFor="guest-name" className="text-sm">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="guest-name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      className="pl-10 h-10"
                      required
                      data-testid="guest-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="guest-email" className="text-sm">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="guest-email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10 h-10"
                      required
                      data-testid="guest-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="guest-phone" className="text-sm">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="guest-phone"
                      type="tel"
                      placeholder="0412 345 678"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="pl-10 h-10"
                      required
                      data-testid="guest-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide pt-2">Address</p>
                
                <div className="space-y-1.5">
                  <Label htmlFor="guest-address" className="text-sm">Street Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="guest-address"
                      type="text"
                      placeholder="123 Example Street"
                      value={formData.address_line}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_line: e.target.value }))}
                      className="pl-10 h-10"
                      required
                      data-testid="guest-address-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="guest-city" className="text-sm">City/Suburb *</Label>
                    <Input
                      id="guest-city"
                      type="text"
                      placeholder="Brisbane"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="h-10"
                      required
                      data-testid="guest-city-input"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="guest-postcode" className="text-sm">Postcode *</Label>
                    <Input
                      id="guest-postcode"
                      type="text"
                      placeholder="4000"
                      value={formData.postcode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                      className="h-10"
                      maxLength={4}
                      required
                      data-testid="guest-postcode-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="guest-state" className="text-sm">State *</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
                  >
                    <SelectTrigger className="h-10" data-testid="guest-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-3 space-y-3">
                <Button
                  type="submit"
                  disabled={loading || !formData.email || !formData.full_name || !formData.phone || 
                           !formData.address_line || !formData.city || !formData.state || !formData.postcode}
                  className="w-full bg-[#004B8D] hover:bg-[#003a6d] h-11"
                  data-testid="guest-continue-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Creating account...
                    </div>
                  ) : (
                    <>
                      Continue to Verification
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('choose')}
                  className="w-full"
                >
                  Back
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center pt-2">
                By continuing, you agree to HireHub's{' '}
                <a href="/legal/terms" className="text-[#004B8D] hover:underline" target="_blank" rel="noopener noreferrer">Terms</a>
                {' '}and{' '}
                <a href="/legal/privacy" className="text-[#004B8D] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GuestCheckoutModal;
